import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import {
  fetchLiveQuestions,
  createLiveQuestion,
  updateLiveQuestion,
  deleteLiveQuestion,
} from "../services/eventsService";
import { showConfirmationDialog } from "../../utils/apiHelper";

// Curiouzz Live, phase three: the coaster's question set. Lives on the
// Curiouzz Social page (founder 2026-09-14). Short lines that survive being
// overheard at a bar; nothing that asks for a job, a street or an ex.
// Inactive lines stay for history and are never served.

const LiveQuestionsPanel = () => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  // The coaster reveal clock (founder 2026-09-15): minutes after two phones
  // link before "Reveal, together?" is offered. Stored with the admin
  // settings, edited here because it belongs to the room, not the flags.
  const [revealMinutes, setRevealMinutes] = useState("10");
  const [revealSaving, setRevealSaving] = useState(false);

  const loadReveal = async () => {
    try {
      const res = await axiosInstance.get("/admin/settings");
      const n = res.data?.liveCoaster?.revealAfterMinutes;
      if (Number.isInteger(n)) setRevealMinutes(String(n));
    } catch (err) {}
  };

  const saveReveal = async () => {
    const n = Number(revealMinutes);
    if (!Number.isInteger(n) || n < 1 || n > 120) {
      setMessage({ ok: false, text: "The reveal clock must be a whole number of minutes between 1 and 120" });
      return;
    }
    try {
      setRevealSaving(true);
      await axiosInstance.put("/admin/settings", { liveCoaster: { revealAfterMinutes: n } });
      setMessage({ ok: true, text: `Reveal clock saved: ${n} minute${n === 1 ? "" : "s"}. Live for every open room within a minute.` });
    } catch (err) {
      setMessage({ ok: false, text: err?.response?.data?.message || "Could not save the reveal clock" });
    } finally {
      setRevealSaving(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetchLiveQuestions();
      if (res.data?.success) setItems(res.data.questions || []);
    } catch (err) {
      setMessage({ ok: false, text: "Failed to load the live question set" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadReveal();
  }, []);

  const add = async () => {
    const t = text.trim();
    if (t.length < 8) return;
    try {
      const res = await createLiveQuestion({ text: t });
      if (res.data?.success) {
        setText("");
        setMessage({ ok: true, text: "Added" });
        load();
      }
    } catch (err) {
      setMessage({ ok: false, text: err?.response?.data?.message || "Could not add" });
    }
  };

  const toggle = async (q) => {
    try {
      await updateLiveQuestion(q._id, { active: !q.active });
      load();
    } catch (err) {
      setMessage({ ok: false, text: "Could not change that line" });
    }
  };

  const edit = async (q) => {
    const next = window.prompt("Edit the line", q.text);
    if (next === null || next.trim() === q.text) return;
    try {
      const res = await updateLiveQuestion(q._id, { text: next });
      if (res.data?.success) load();
    } catch (err) {
      setMessage({ ok: false, text: err?.response?.data?.message || "Could not save" });
    }
  };

  const remove = async (q) => {
    const result = await showConfirmationDialog("Delete this line for good? Turning it off keeps it for later.");
    if (!result?.isConfirmed) return;
    try {
      await deleteLiveQuestion(q._id);
      load();
    } catch (err) {
      setMessage({ ok: false, text: "Could not delete" });
    }
  };

  const active = items.filter((q) => q.active).length;

  return (
    <div className="card basic-data-table mt-24">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-0">Coaster questions</h5>
          <small className="text-secondary-light">
            The live set for the digital coaster. {active} active of {items.length}. A line served to one pair in a
            room is held back from every other pair in that room for 20 minutes.
          </small>
        </div>
      </div>
      <div className="card-body">
        {message && (
          <div className={`alert ${message.ok ? "alert-success" : "alert-danger"} py-2`} role="alert">
            {message.text}
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between py-12 mb-16" style={{ borderBottom: "1px solid #e9ecef" }}>
          <div style={{ paddingRight: 24 }}>
            <p className="fw-semibold mb-1">Reveal opens after</p>
            <small className="text-secondary-light">
              Minutes after two phones link on a coaster before "Reveal, together?" is offered. Ten is the default. A
              change reaches every open room within a minute, no release needed.
            </small>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <input
              type="number"
              min={1}
              max={120}
              className="form-control"
              style={{ width: 90 }}
              value={revealMinutes}
              onChange={(e) => setRevealMinutes(e.target.value)}
            />
            <span className="text-secondary-light">min</span>
            <button className="btn btn-outline-primary btn-sm" onClick={saveReveal} disabled={revealSaving}>
              {revealSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
        <div className="d-flex gap-2 mb-16">
          <input
            className="form-control"
            placeholder="A new line. Short, warm, fine to be overheard."
            value={text}
            maxLength={140}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <button className="btn btn-primary btn-sm" onClick={add} disabled={text.trim().length < 8}>
            Add
          </button>
        </div>
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Line</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 230 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4}>Loading…</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4}>No lines yet. The coaster cannot deal a card until there is at least one.</td>
                </tr>
              ) : (
                items.map((q, i) => (
                  <tr key={q._id} style={{ opacity: q.active ? 1 : 0.55 }}>
                    <td>{i + 1}</td>
                    <td>{q.text}</td>
                    <td>
                      <span
                        className={`badge ${
                          q.active ? "bg-success-focus text-success-main" : "bg-neutral-200 text-neutral-600"
                        } px-12 py-4 radius-4`}
                      >
                        {q.active ? "active" : "off"}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-outline-primary btn-sm me-2" onClick={() => toggle(q)}>
                        {q.active ? "Turn off" : "Turn on"}
                      </button>
                      <button className="btn btn-outline-secondary btn-sm me-2" onClick={() => edit(q)}>
                        Edit
                      </button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => remove(q)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LiveQuestionsPanel;
